/**
 * companion.js — Cycle 56 · Part 2 (companion picker) + memory hooks
 * ─────────────────────────────────────────────────────────────────
 * The child chooses ONE companion at onboarding. The choice is
 * consistent across streaks, celebrations, empty states, and the
 * AI tutor (Ask the Globe) voice. Stored on the child record.
 *
 * MEMORY: uses the learner model (learnerModel.js) as its data
 * source. No new tracking.
 */
import { getActiveChild, updateChild } from "@/lib/children";
import { computeLearnerModel } from "@/lib/learnerModel";

// Curated safe designs + curated safe names. No free text.
// COMPANION_DESIGNS — the child's *selectable* companion. Compass is
// the platform-wide guide/narrator (system voice) and lives outside
// this list. Every character below is a companion the child can
// choose to travel with. Keep exactly one entry per id.
export const COMPANION_DESIGNS = [
  { id: "globy",   name: "Globy",   emoji: "🌍", color: "bg-sky-200",    intro: "Loves countries, flags and adventures." },
  { id: "foxy",    name: "Foxy",    emoji: "🦊", color: "bg-orange-200", intro: "Curious and quick with a plan." },
  { id: "panda",   name: "Bao",     emoji: "🐼", color: "bg-slate-200",  intro: "Calm and thoughtful — never rushed." },
  { id: "koala",   name: "Kip",     emoji: "🐨", color: "bg-emerald-200",intro: "Chill, patient and encouraging." },
  { id: "owl",     name: "Otis",    emoji: "🦉", color: "bg-indigo-200", intro: "Bookish, loves a good question." },
  { id: "cat",     name: "Momo",    emoji: "🐱", color: "bg-rose-200",   intro: "Playful and always up for a puzzle." },
  { id: "dragon",  name: "Zephyr",  emoji: "🐉", color: "bg-fuchsia-200",intro: "Brave and imaginative." },
  { id: "star",    name: "Nova",    emoji: "⭐", color: "bg-yellow-200", intro: "Bright, cheerful, always ready to celebrate a win." },
];

// Legacy note — profiles saved before Feb-2026 may have `companionId`
// of "compass". `getCompanion()` treats that as "no companion picked"
// so the child is nudged back through the picker.
const LEGACY_COMPASS_ID = "compass";

export const SAFE_NAMES = [
  "Globy", "Foxy", "Bao", "Kip", "Otis", "Momo", "Zephyr", "Nova",
  "Pip", "Sage", "Rune", "Echo", "Milo",
  "Star", "Sunny", "Bramble", "Willow", "Marlow", "Fern",
];

// Feb 2026 · Custom-name support with client-side safety filter.
// Kids can pick a name outside the SAFE_NAMES list — but only if the
// input passes a strict allow-list of letters/apostrophes/hyphens AND
// misses every entry in the profanity/PII deny-list. No network call:
// the whole check runs offline so it works on airplane mode.
const DENYLIST = new Set([
  // Profanity / offensive terms (very short indicative list — err on
  // the side of caution; the SAFE_NAMES fallback catches misses).
  "fuck","shit","bitch","damn","cunt","piss","dick","cock","pussy",
  "arse","ass","asshole","bastard","bollocks","crap","fag","faggot",
  "slut","whore","tits","twat","wanker","hell","nazi","hitler",
  // Common brand/PII terms we don't want as companion names.
  "google","facebook","instagram","tiktok","youtube","snapchat",
  "twitter","reddit","apple","amazon","netflix","microsoft",
  "admin","root","system","null","undefined","test","debug",
]);

/** Sanitize a user-typed companion name. Returns `{ ok, name, reason }`. */
export function sanitizeCustomName(raw) {
  if (raw == null) return { ok: false, name: "", reason: "Type a name." };
  const trimmed = String(raw).trim();
  if (trimmed.length < 2) return { ok: false, name: trimmed, reason: "A little longer, please." };
  if (trimmed.length > 12) return { ok: false, name: trimmed, reason: "Keep it under 12 letters." };
  // Allow letters + apostrophe + hyphen only — no digits, no spaces,
  // no punctuation, no emoji. Single word only.
  if (!/^[A-Za-z][A-Za-z'\-]*$/.test(trimmed)) {
    return { ok: false, name: trimmed, reason: "Letters only — no numbers or spaces." };
  }
  if (DENYLIST.has(trimmed.toLowerCase())) {
    return { ok: false, name: trimmed, reason: "Pick a friendlier name." };
  }
  // Title-case it so "milo" → "Milo" for a consistent display.
  const nice = trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
  return { ok: true, name: nice, reason: "" };
}

const LS_ID   = "we_companion_id_v1";
const LS_NAME = "we_companion_name_v1";

const safeGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const safeSet = (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } };

/** Get the currently-chosen companion (or a default). Legacy profiles
 *  that saved `companionId: "compass"` (before Feb-2026, when Compass
 *  was still selectable) are treated as "not chosen" so the child is
 *  gently nudged into the picker to pick a real companion. Compass
 *  remains the platform-wide guide/narrator, never a companion. */
export function getCompanion() {
  const child = getActiveChild();
  const rawId = child?.companionId || safeGet(LS_ID) || null;
  const isLegacyCompass = rawId === LEGACY_COMPASS_ID;
  const id = (!rawId || isLegacyCompass) ? COMPANION_DESIGNS[0].id : rawId;
  const design = COMPANION_DESIGNS.find((d) => d.id === id) || COMPANION_DESIGNS[0];
  const name = child?.companionName || safeGet(LS_NAME) || design.name;
  // `chosen` remains false for legacy-Compass so the picker still fires.
  return { ...design, name, chosen: !!(rawId && !isLegacyCompass) };
}

/** Set the companion — id + optional custom name. The name may be
 *  either from `SAFE_NAMES` OR any user-typed string that passes
 *  `sanitizeCustomName()`. Anything else falls back to the default. */
export function setCompanion({ id, name }) {
  const design = COMPANION_DESIGNS.find((d) => d.id === id);
  if (!design) return null;
  let safeName = design.name;
  if (name) {
    if (SAFE_NAMES.includes(name)) {
      safeName = name;
    } else {
      const sanitized = sanitizeCustomName(name);
      if (sanitized.ok) safeName = sanitized.name;
    }
  }
  safeSet(LS_ID, id);
  safeSet(LS_NAME, safeName);
  const child = getActiveChild();
  if (child) updateChild(child.id, { companionId: id, companionName: safeName });
  try { window.dispatchEvent(new CustomEvent("we-companion-changed", { detail: { id, name: safeName } })); } catch { /* ignore */ }
  return { id, name: safeName };
}

/**
 * Companion greeting on return — grounded in real recent data.
 * Not a network call: we synthesise from the learner model client-side.
 * The tutor endpoint (server) receives this as context too.
 */
export function greetingLine() {
  const c = getCompanion();
  const m = computeLearnerModel();
  const firstName = m?.childName || "friend";
  const openings = [
    `Hey ${firstName}! I'm ${c.name}.`,
    `Hi ${firstName} — ${c.name} here.`,
    `Welcome back, ${firstName}. ${c.name} at the ready.`,
  ];
  const opener = openings[Math.floor(Math.random() * openings.length)];

  const parts = [opener];

  if (m?.daysSinceLast === null) {
    parts.push("Ready to start our journey?");
  } else if (m.daysSinceLast > 14) {
    parts.push(`It's been a while — great to see you. No pressure, we'll pick up gently.`);
  } else if (m.daysSinceLast >= 1 && m.recentMastered?.length) {
    const skill = m.recentMastered[0].skillId?.split("-").slice(-1)[0] || "your last lesson";
    parts.push(`You mastered ${skill} recently — want to try the next step?`);
  } else if (m.streak > 1) {
    parts.push(`That's day ${m.streak} of your streak — proud of you.`);
  } else {
    parts.push("What shall we explore today?");
  }
  return parts.join(" ");
}

/** Tutor context line — plugged into /api/teacher `learner_context`. */
export function tutorContext() {
  const c = getCompanion();
  const m = computeLearnerModel();
  const lines = [];
  lines.push(`Speak as the child's chosen companion "${c.name}" (${c.id}), a warm learning guide (never claim to be human).`);
  if (m?.childName) lines.push(`Child's name: ${m.childName}.`);
  if (m?.recentMastered?.length) {
    lines.push(`They recently mastered: ${m.recentMastered.slice(0, 3).map((r) => r.skillId).join(", ")}.`);
  }
  if (m?.errorPatterns?.length) {
    lines.push(`Known error pattern(s) to address specifically: ${m.errorPatterns.join(", ")}.`);
  }
  if (m?.preferredDelivery) lines.push(`Learns best via ${m.preferredDelivery.replace(/_/g, " ")}.`);
  if (m?.aspiration) lines.push(`Aspiration: ${m.aspiration} — connect big ideas to this when it fits.`);
  if (m?.interests?.length) lines.push(`Interests: ${m.interests.slice(0, 3).join(", ")}.`);
  return lines.join(" ").slice(0, 700);
}
